import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  Stack,
  alpha,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  Favorite,
  CheckCircle,
  Close,
  CalendarToday,
  Height,
  ContactPhone,
  Email,
} from "@mui/icons-material";
import { useWishlist } from "../contexts/WishlistContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import apiEndpoints from "../apiConfig";
import { People, Person, Info } from "@mui/icons-material";

function ProfileDetailPage() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { showToast } = useToast();
  const { user } = useAuth();
  const theme = useTheme();

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // < 900px
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg")); // 900px - 1200px
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  const getProfilePhoto = (photo) =>
    photo
      ? photo.startsWith("http")
        ? photo.trim()
        : `http://localhost/matrimony_user/${photo.trim()}`
      : "/placeholder.svg";

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const currentUserParam = user ? `&current_user_id=${user.id}` : "";
        const res = await fetch(
          `${apiEndpoints.register}?action=fetch&user_id=${user_id}${currentUserParam}`,
        );
        const data = await res.json();

        if (data && !data.status) {
          setProfile({
            ...data,
            photos: [
              data.profile_photo,
              data.profile_photo1,
              data.profile_photo2,
              data.profile_photo3,
            ]
              .filter(Boolean)
              .map(getProfilePhoto),
          });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user_id, user]);

  if (loading)
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );

  if (!profile)
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h5" color="text.secondary">
          Profile not found
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/home")}
          sx={{ mt: 2 }}
        >
          Back to Home
        </Button>
      </Container>
    );

  const inWishlist = isInWishlist(profile.id);

  const handleWishlistToggle = () => {
    if (inWishlist) removeFromWishlist(profile.id);
    else addToWishlist(profile);
  };

  const InfoRow = ({ label, value }) =>
    value && value !== "-" ? (
      <Grid container spacing={1} sx={{ py: 0.5 }}>
        <Grid item xs={5} sm={4}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: {
                xs: "0.75rem",
                sm: "0.875rem",
              },
            }}
          >
            {label}:
          </Typography>
        </Grid>
        <Grid item xs={7} sm={8}>
          <Typography
            variant="body1"
            sx={{
              fontSize: {
                xs: "0.75rem",
                sm: "0.875rem",
              },
            }}
          >
            {value}
          </Typography>
        </Grid>
      </Grid>
    ) : null;

  const safeValue = (val) => (val ? val : "-");

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      {/* Header */}
      <Container
        maxWidth={false}
        sx={{ py: { xs: 1, sm: 2 }, px: { xs: 2, sm: 3 } }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/home")}
          color="inherit"
          sx={{
            color: "black",
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
            minWidth: "auto",
          }}
        >
          {isSmallMobile ? "" : "Back"}
        </Button>
      </Container>

      {/* Main Content */}
      <Container
        maxWidth={false}
        sx={{
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Grid
          container
          spacing={3}
          width={"100%"}
          direction={isMobile ? "column" : "row"}
          wrap={isMobile ? "wrap" : "nowrap"}
        >
          {/* Left Column - Photos & Basic Info */}
          <Grid
            item
            xs={12}
            md={4}
            lg={3}
            sx={{
              width: isMobile ? "100%" : "50%",
              flexShrink: isMobile ? 1 : 0,
            }}
          >
            <Stack spacing={2}>
              {/* Photo Card */}
              <Card>
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    height={isMobile ? 250 : 300}
                    image={profile.photos[selectedImage] || "/placeholder.svg"}
                    alt={`${profile.name}'s photo`}
                    sx={{
                      objectFit: "cover",
                      cursor: "pointer",
                      width: "100%",
                    }}
                    onClick={() => setImageDialogOpen(true)}
                  />
                  {profile.isVerified && (
                    <Chip
                      icon={<CheckCircle />}
                      label="Verified"
                      size="small"
                      color="success"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    />
                  )}
                </Box>

                {/* Thumbnails */}
                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                  <Grid container spacing={1}>
                    {profile.photos.map((photo, idx) => (
                      <Grid item xs={3} key={idx}>
                        <Box
                          component="img"
                          src={photo}
                          alt={`Thumbnail ${idx + 1}`}
                          sx={{
                            width: "100%",
                            height: isMobile ? 50 : 60,
                            objectFit: "cover",
                            borderRadius: 1,
                            cursor: "pointer",
                            border: selectedImage === idx ? 2 : 1,
                            borderColor:
                              selectedImage === idx
                                ? "primary.main"
                                : "grey.300",
                          }}
                          onClick={() => setSelectedImage(idx)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Card>

              {/* Basic Info */}
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    sx={{
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.1rem",
                        sm: "1.25rem",
                        md: "1.5rem",
                      },
                    }}
                  >
                    {profile.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: isSmallMobile ? "column" : "row",
                      flexWrap: "wrap",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    {profile.age && (
                      <Typography
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <CalendarToday sx={{ fontSize: 16, mr: 0.5 }} />
                        {profile.age} years
                      </Typography>
                    )}
                    {profile.height && (
                      <Typography
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Height sx={{ fontSize: 16, mr: 0.5 }} />
                        {profile.height}
                      </Typography>
                    )}
                  </Box>

                  {/* Wishlist Button */}
                  <Button
                    variant={inWishlist ? "contained" : "outlined"}
                    color="secondary"
                    startIcon={<Favorite />}
                    onClick={handleWishlistToggle}
                    fullWidth
                    sx={{
                      mt: 2,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    {inWishlist ? "Remove from Wishlist" : "Add to Wishlist"}
                  </Button>
                </CardContent>
              </Card>

              {/* Contact Details Section (Only if Available) */}
              {(profile.phone || profile.email) && (
                <Card sx={{ border: "1px solid", borderColor: "success.main" }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <ContactPhone color="success" />
                      <Typography
                        variant="h6"
                        fontWeight="700"
                        color="success.main"
                      >
                        Contact Details
                      </Typography>
                    </Box>
                    <Stack spacing={1}>
                      {profile.phone && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <ContactPhone fontSize="small" color="action" />
                          <Typography variant="body1">
                            {profile.phone}
                          </Typography>
                        </Box>
                      )}
                      {profile.email && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Email fontSize="small" color="action" />
                          <Typography variant="body1">
                            {profile.email}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* About Me */}
              {profile.about_me && (
                <Card>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Info
                        sx={{ color: "black", fontSize: { xs: 18, sm: 20 } }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "black",
                          fontSize: { xs: "1rem", sm: "1.1rem" },
                        }}
                      >
                        About Me
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        lineHeight: 1.5,
                        height: "75px",
                        maxHeight: 400,
                        overflow: "auto",
                      }}
                    >
                      {profile.about_me || "-"}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>

          {/* Right Column - Details */}
          <Grid
            item
            xs={12}
            md={8}
            lg={9}
            sx={{
              width: isMobile ? "100%" : "50%",
              pl: isMobile ? 0 : "1%",
            }}
          >
            <Stack spacing={2}>
              {/* Personal Details */}
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Person
                      sx={{ color: "black", fontSize: { xs: 18, sm: 20 } }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "black",
                        fontSize: { xs: "1rem", sm: "1.1rem" },
                      }}
                    >
                      Personal Details
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label="Religion"
                        value={safeValue(profile.religion)}
                      />
                      <InfoRow label="Caste" value={safeValue(profile.caste)} />
                      <InfoRow
                        label="Blood Group"
                        value={safeValue(profile.blood_group)}
                      />
                      <InfoRow
                        label="Marital Status"
                        value={safeValue(profile.marital_status)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label="Location"
                        value={`${safeValue(profile.state)}, ${safeValue(profile.country)}`}
                      />
                      <InfoRow
                        label="Education"
                        value={safeValue(profile.education)}
                      />
                      <InfoRow
                        label="Profession"
                        value={safeValue(profile.profession)}
                      />
                      <InfoRow
                        label="Annual Income"
                        value={safeValue(profile.annual_income)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Family Details */}
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <People
                      sx={{ color: "black", fontSize: { xs: 18, sm: 20 } }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "black",
                        fontSize: { xs: "1rem", sm: "1.1rem" },
                      }}
                    >
                      Family Details
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label="Father's Name"
                        value={safeValue(profile.father_name)}
                      />
                      <InfoRow
                        label="Father's Occupation"
                        value={safeValue(profile.father_occupation)}
                      />
                      <InfoRow
                        label="Mother's Name"
                        value={safeValue(profile.mother_name)}
                      />
                      <InfoRow
                        label="Mother's Occupation"
                        value={safeValue(profile.mother_occupation)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label="Siblings"
                        value={safeValue(profile.siblings)}
                      />
                      <InfoRow
                        label="Family Type"
                        value={safeValue(profile.family_type)}
                      />
                      <InfoRow
                        label="Family Status"
                        value={safeValue(profile.family_status)}
                      />
                      <InfoRow
                        label="About Family"
                        value={safeValue(profile.about_family)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Partner Preferences */}
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Favorite
                      sx={{ color: "black", fontSize: { xs: 18, sm: 20 } }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "black",
                        fontSize: { xs: "1rem", sm: "1.1rem" },
                      }}
                    >
                      Partner Preferences
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label="Religion"
                        value={safeValue(profile.pref_religion)}
                      />
                      <InfoRow
                        label="Education"
                        value={safeValue(profile.pref_education)}
                      />
                      <InfoRow
                        label="Profession"
                        value={safeValue(profile.pref_profession)}
                      />
                      <InfoRow
                        label="Caste"
                        value={safeValue(profile.pref_caste)}
                      />
                      <InfoRow
                        label="Blood Group"
                        value={safeValue(profile.pref_blood_group)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label="Marital Status"
                        value={safeValue(profile.pref_marital_status)}
                      />
                      <InfoRow
                        label="Annual Income"
                        value={safeValue(profile.pref_annual_income)}
                      />
                      <InfoRow
                        label="Location"
                        value={`${safeValue(profile.pref_city)}, ${safeValue(
                          profile.pref_state,
                        )}, ${safeValue(profile.pref_country)}`}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile} // Fullscreen on mobile
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={() => setImageDialogOpen(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "white",
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: isMobile ? "100vh" : "auto",
              backgroundColor: "black",
            }}
          >
            <img
              src={profile.photos[selectedImage] || "/placeholder.svg"}
              alt={`${profile.name}'s photo`}
              style={{
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "auto" : "80vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default ProfileDetailPage;
